import { type FetchBaseQueryError } from "@reduxjs/toolkit/query";

import { type ExecuteBaseQuery } from "../../../shared/api/create-query-fn";
import { normalizeApiError } from "../../../shared/utils/normalize-api-error";
import { TechOperation } from "../shared/models/tech-operation";

const UPSERT_RESPONSE_SHAPE_ERROR: FetchBaseQueryError = {
  status: "CUSTOM_ERROR",
  error: "Произошла ошибка. Попробуйте ещё раз или обратитесь в техподдержку.",
};
const UPSERT_VALIDATION_ERROR_MESSAGE_PREFIX =
  "Ошибка валидации при сохранении агроработы. Скопируйте текст ниже и отправьте в техподдержку:";
const UPSERT_BASE_QUERY_ERROR_MESSAGE_PREFIX =
  "Произошла ошибка при сохранении агроработы. Скопируйте текст ниже и отправьте в техподдержку:";

interface IUpsertTechOperationValidationErrorDto {
  propertyName: string;
  errorMessage: string;
}

interface IUpsertTechOperationInvalidItemDto {
  techOperation: { id: string };
  validationResult: { errors: IUpsertTechOperationValidationErrorDto[] };
}

interface IUpsertTechOperationsResultDto {
  invalid: IUpsertTechOperationInvalidItemDto[];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isUpsertTechOperationsResultDto(
  value: unknown
): value is IUpsertTechOperationsResultDto {
  return isRecord(value) && Array.isArray(value.invalid);
}

function buildUpsertValidationError(
  invalidItems: IUpsertTechOperationInvalidItemDto[]
): FetchBaseQueryError {
  const details = invalidItems.map((item) => ({
    techOperationId: item.techOperation.id,
    errors: item.validationResult.errors.map((error) => ({
      field: error.propertyName,
      message: error.errorMessage,
    })),
  }));

  return {
    status: "CUSTOM_ERROR",
    error: `${UPSERT_VALIDATION_ERROR_MESSAGE_PREFIX}\n${JSON.stringify(details)}`,
  };
}

function buildUpsertBaseQueryError(error: FetchBaseQueryError): FetchBaseQueryError {
  const { message } = normalizeApiError(error).error;
  return {
    status: "CUSTOM_ERROR",
    error: `${UPSERT_BASE_QUERY_ERROR_MESSAGE_PREFIX}\n${message}`,
  };
}

export async function executeUpsertTechOp(
  techOpModel: TechOperation,
  executeBaseQuery: ExecuteBaseQuery
) {
  const response = await executeBaseQuery({
    url: "TechOperationsV2",
    method: "POST",
    headers: {
      "Content-Type": "application/json;charset=utf-8",
    },
    body: JSON.stringify([techOpModel.asUpsertDto]),
  });

  if (response.error !== undefined) {
    return { error: buildUpsertBaseQueryError(response.error) };
  }

  if (!isUpsertTechOperationsResultDto(response.data)) {
    return { error: UPSERT_RESPONSE_SHAPE_ERROR };
  }

  if (response.data.invalid.length > 0) {
    return { error: buildUpsertValidationError(response.data.invalid) };
  }

  return { data: techOpModel };
}
